<template>
  <div class="district-map-picker">
    <!-- Search & Control Header -->
    <div class="map-controls-header" v-if="!readonly">
      <div class="search-box-wrapper">
        <div class="search-input-inner">
          <i class="fas fa-search search-icon"></i>
          <input
            type="text"
            ref="autocompleteInput"
            id="district_places_autocomplete"
            class="form-control map-search-input"
            :placeholder="
              lang === 'ar'
                ? 'ابحث عن موقع أو حي في الخريطة...'
                : 'Search location or neighborhood...'
            "
            @focus="setupAutocomplete"
          />
        </div>
      </div>

      <div class="controls-toolbar">
        <!-- Mode Switch: Polygon Drawing, Move Area, or Center Pin -->
        <div class="mode-toggle-group">
          <button
            type="button"
            class="mode-btn"
            :class="{ active: activeMode === 'polygon' }"
            @click="setMode('polygon')"
            :title="
              lang === 'ar'
                ? 'وضع رسم وتعديل نقاط الحدود'
                : 'Draw & Edit Points'
            "
          >
            <i class="fas fa-draw-polygon"></i>
            <span>{{
              lang === "ar" ? "رسم وتعديل النقاط" : "Edit Points"
            }}</span>
          </button>

          <button
            type="button"
            class="mode-btn"
            :class="{ active: activeMode === 'move' }"
            @click="setMode('move')"
            :disabled="markers.length < 3"
            :title="
              lang === 'ar'
                ? 'وضع سحب وتحريك المنطقة بالكامل'
                : 'Drag Whole Area'
            "
          >
            <i class="fas fa-arrows-alt"></i>
            <span>{{
              lang === "ar" ? "سحب المنطقة كاملة" : "Drag Whole Area"
            }}</span>
          </button>

          <button
            type="button"
            class="mode-btn"
            :class="{ active: activeMode === 'center' }"
            @click="setMode('center')"
            :title="
              lang === 'ar' ? 'وضع تحديد مركز الحي' : 'District Center Mode'
            "
          >
            <i class="fas fa-map-pin"></i>
            <span>{{ lang === "ar" ? "مركز الحي" : "District Center" }}</span>
          </button>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons-group">
          <button
            type="button"
            class="btn-tool btn-undo"
            @click="undoLastPoint"
            :disabled="markers.length === 0"
            :title="lang === 'ar' ? 'حذف آخر نقطة' : 'Undo Last Point'"
          >
            <i class="fas fa-undo"></i>
            <span class="d-none d-sm-inline">{{
              lang === "ar" ? "تراجع" : "Undo"
            }}</span>
          </button>

          <button
            type="button"
            class="btn-tool btn-clear"
            @click="clearAllPoints"
            :disabled="markers.length === 0"
            :title="
              lang === 'ar'
                ? 'مسح جميع النقاط وإعادة الرسم'
                : 'Clear All Points'
            "
          >
            <i class="fas fa-trash-alt"></i>
            <span class="d-none d-sm-inline">{{
              lang === "ar" ? "إعادة رسم" : "Reset"
            }}</span>
          </button>

          <button
            type="button"
            class="btn-tool btn-center"
            @click="fitBoundsToPolygon"
            :disabled="markers.length === 0"
            :title="lang === 'ar' ? 'توسيط الخريطة على الحي' : 'Fit to Polygon'"
          >
            <i class="fas fa-expand"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Status Indicator Bar -->
    <div class="map-status-bar">
      <div
        class="points-badge"
        :class="hasMinPoints ? 'badge-valid' : 'badge-warning'"
      >
        <i
          :class="
            hasMinPoints ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'
          "
        ></i>
        <span>
          {{
            lang === "ar"
              ? `عدد النقاط: ${markers.length}`
              : `Points count: ${markers.length}`
          }}
          <template v-if="!hasMinPoints">
            ({{
              lang === "ar"
                ? "الحد الأدنى 4 نقاط"
                : "Minimum 4 points required"
            }})
          </template>
          <template v-else>
            ({{ lang === "ar" ? "مكتمل" : "Valid" }})
          </template>
        </span>
      </div>

      <div class="center-info-badge" v-if="centerPoint.lat && centerPoint.lng">
        <i class="fas fa-map-marker-alt text-danger"></i>
        <span>
          {{ lang === "ar" ? "المركز:" : "Center:" }}
          {{ Number(centerPoint.lat).toFixed(4) }},
          {{ Number(centerPoint.lng).toFixed(4) }}
        </span>
      </div>

      <div class="mode-hint">
        <small v-if="activeMode === 'polygon'">
          <i class="fas fa-info-circle text-primary"></i>
          {{
            lang === "ar"
              ? "انقر لإضافة نقاط، اسحب أي نقطة بسلاسة لتعديلها، أو انقر عليها لحذفها"
              : "Click map to add points, drag points smoothly to adjust, or click a point to remove"
          }}
        </small>
        <small v-else-if="activeMode === 'move'">
          <i class="fas fa-arrows-alt text-success"></i>
          {{
            lang === "ar"
              ? "اضغط داخل المضلع واسحب لتحريك المنطقة بالكامل إلى موقع جديد"
              : "Click inside the polygon and drag to move the entire area smoothly"
          }}
        </small>
        <small v-else>
          <i class="fas fa-info-circle text-warning"></i>
          {{
            lang === "ar"
              ? "انقر على الخريطة أو اسحب العلامة الحمراء لتحديد مركز الحي"
              : "Click on map or drag red pin to position district center"
          }}
        </small>
      </div>
    </div>

    <!-- Google Map View -->
    <div
      class="map-container-frame"
      :class="{ 'mode-move-active': activeMode === 'move' }"
      :style="{ height: height }"
    >
      <GmapMap
        ref="gmap"
        :center="mapCenter"
        :zoom="zoom"
        :options="mapOptions"
        style="width: 100%; height: 100%"
        @click="handleMapClick"
      >
        <!-- Render Polygon: supports whole area dragging -->
        <gmap-polygon
          ref="gmapPolygon"
          v-if="markers.length >= 2"
          :paths="markers"
          :draggable="!readonly && activeMode === 'move'"
          :options="{
            strokeColor: strokeColor,
            strokeOpacity: 0.9,
            strokeWeight: 3,
            fillColor: fillColor,
            fillOpacity: activeMode === 'move' ? 0.45 : 0.35,
            clickable: true,
            draggable: !readonly && activeMode === 'move',
            zIndex: 2,
            cursor: activeMode === 'move' ? 'grab' : 'pointer',
          }"
          @dragend="onPolygonDragEnd"
          @click="handlePolygonClick"
        />

        <!-- Polygon Vertex Markers (Draggable & Click-to-remove) -->
        <GmapMarker
          v-for="(marker, index) in markers"
          :key="`vertex-${index}`"
          :position="marker"
          :draggable="!readonly && activeMode === 'polygon'"
          :label="{
            text: `${index + 1}`,
            color: '#FFFFFF',
            fontSize: '11px',
            fontWeight: 'bold',
          }"
          :icon="{
            path: 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z',
            fillColor: strokeColor,
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 1.5,
            scale: 0.8,
            labelOrigin: { x: 0, y: -30 },
          }"
          :title="
            lang === 'ar'
              ? `نقطة ${index + 1} (اسحب للتعديل، انقر للحذف)`
              : `Point ${index + 1} (Drag to move, click to delete)`
          "
          @drag="onVertexDrag(index, $event)"
          @dragend="onVertexDragEnd(index, $event)"
          @click="removePoint(index)"
        />

        <!-- District Center Pin -->
        <GmapMarker
          v-if="centerPoint.lat && centerPoint.lng"
          :position="centerPoint"
          :draggable="
            !readonly && (activeMode === 'center' || activeMode === 'polygon')
          "
          :title="
            lang === 'ar'
              ? 'مركز الحي (اسحب للتغيير)'
              : 'District Center (Drag to reposition)'
          "
          :icon="{
            path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
            fillColor: '#E53935',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
            scale: 1.6,
            anchor: { x: 12, y: 22 },
          }"
          @drag="onCenterDrag($event)"
          @dragend="onCenterDragEnd($event)"
        />
      </GmapMap>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "DistrictMapPicker",

  props: {
    initialArea: {
      type: Array,
      default: () => [],
    },
    initialCenter: {
      type: Object,
      default: () => ({ lat: null, lng: null }),
    },
    height: {
      type: String,
      default: "480px",
    },
    strokeColor: {
      type: String,
      default: "#783D73",
    },
    fillColor: {
      type: String,
      default: "#783D73",
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      markers: [],
      centerPoint: { lat: null, lng: null },
      mapCenter: { lat: 24.7136, lng: 46.6753 }, // Riyadh default
      zoom: 11,
      activeMode: "polygon", // 'polygon' | 'move' | 'center'
      autocompleteInitialized: false,
      dragAnimFrame: null,
      mapOptions: {
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: true,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        gestureHandling: "greedy",
      },
    };
  },

  computed: {
    ...mapGetters({
      lang: "lang_module/lang",
    }),

    hasMinPoints() {
      return this.markers.length >= 4;
    },
  },

  watch: {
    initialArea: {
      handler(newArea) {
        if (Array.isArray(newArea) && newArea.length > 0) {
          this.markers = newArea.map((p) => ({
            lat: parseFloat(p.lat),
            lng: parseFloat(p.lng),
          }));
          this.$nextTick(() => {
            this.fitBoundsToPolygon();
          });
        }
      },
      immediate: true,
      deep: true,
    },

    initialCenter: {
      handler(newCenter) {
        if (newCenter && newCenter.lat && newCenter.lng) {
          this.centerPoint = {
            lat: parseFloat(newCenter.lat),
            lng: parseFloat(newCenter.lng),
          };
          this.mapCenter = { ...this.centerPoint };
        }
      },
      immediate: true,
      deep: true,
    },
  },

  mounted() {
    this.setupInitialData();
  },

  methods: {
    setMode(mode) {
      this.activeMode = mode;
    },

    setupInitialData() {
      if (this.initialArea && this.initialArea.length > 0) {
        this.markers = this.initialArea.map((p) => ({
          lat: parseFloat(p.lat),
          lng: parseFloat(p.lng),
        }));
      }

      if (
        this.initialCenter &&
        this.initialCenter.lat &&
        this.initialCenter.lng
      ) {
        this.centerPoint = {
          lat: parseFloat(this.initialCenter.lat),
          lng: parseFloat(this.initialCenter.lng),
        };
        this.mapCenter = { ...this.centerPoint };
      } else if (this.markers.length > 0) {
        this.mapCenter = { ...this.markers[0] };
        this.fitBoundsToPolygon();
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            if (!this.markers.length && !this.centerPoint.lat) {
              this.mapCenter = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
            }
          },
          () => {}
        );
      }
    },

    setupAutocomplete() {
      if (
        this.autocompleteInitialized ||
        typeof google === "undefined" ||
        !google.maps ||
        !google.maps.places
      ) {
        return;
      }

      const input = this.$refs.autocompleteInput;
      if (!input) return;

      const searchBox = new google.maps.places.SearchBox(input);
      this.autocompleteInitialized = true;

      searchBox.addListener("places_changed", () => {
        const places = searchBox.getPlaces();
        if (!places || places.length === 0) return;

        const place = places[0];
        if (!place.geometry || !place.geometry.location) return;

        const newLat = place.geometry.location.lat();
        const newLng = place.geometry.location.lng();

        this.mapCenter = { lat: newLat, lng: newLng };
        this.zoom = 14;

        if (this.activeMode === "center" || !this.centerPoint.lat) {
          this.centerPoint = { lat: newLat, lng: newLng };
          this.$emit("center-changed", this.centerPoint);
        }

        if (place.formatted_address) {
          this.$emit("location-changed", place.formatted_address);
        }
      });
    },

    handleMapClick(e) {
      if (this.readonly || this.activeMode === "move") return;

      const clickedPoint = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };

      if (this.activeMode === "polygon") {
        this.markers.push(clickedPoint);
        this.$emit("area-changed", this.markers);

        // If center is not yet set, automatically set it from centroid
        if (!this.centerPoint.lat) {
          this.calculateAndSetCentroid();
        }
      } else if (this.activeMode === "center") {
        this.centerPoint = { ...clickedPoint };
        this.$emit("center-changed", this.centerPoint);
        this.reverseGeocode(this.centerPoint);
      }
    },

    handlePolygonClick(e) {
      if (this.readonly) return;
      if (this.activeMode === "center") {
        this.centerPoint = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        };
        this.$emit("center-changed", this.centerPoint);
        this.reverseGeocode(this.centerPoint);
      } else if (this.activeMode === "polygon") {
        // Append point
        this.markers.push({
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        });
        this.$emit("area-changed", this.markers);
      }
    },

    // Ultra-smooth vertex drag using native Google Maps polygon path update
    onVertexDrag(index, e) {
      if (this.dragAnimFrame) {
        cancelAnimationFrame(this.dragAnimFrame);
      }

      this.dragAnimFrame = requestAnimationFrame(() => {
        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();

        // Update native polygon path directly for 60fps rendering without full Vue re-render
        if (this.$refs.gmapPolygon && this.$refs.gmapPolygon.$polygonObject) {
          const path = this.$refs.gmapPolygon.$polygonObject.getPath();
          if (path && path.getLength() > index) {
            path.setAt(index, new google.maps.LatLng(newLat, newLng));
          }
        }
      });
    },

    onVertexDragEnd(index, e) {
      const updatedPoint = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      this.$set(this.markers, index, updatedPoint);
      this.$emit("area-changed", this.markers);
    },

    // Whole area drag handler: triggered when user drags the entire polygon
    onPolygonDragEnd() {
      if (!this.$refs.gmapPolygon || !this.$refs.gmapPolygon.$polygonObject)
        return;
      const poly = this.$refs.gmapPolygon.$polygonObject;
      const path = poly.getPath();
      if (!path) return;

      const newMarkers = [];
      for (let i = 0; i < path.getLength(); i++) {
        const p = path.getAt(i);
        newMarkers.push({
          lat: p.lat(),
          lng: p.lng(),
        });
      }

      if (newMarkers.length > 0) {
        // Calculate the shift delta to also shift centerPoint smoothly
        if (
          this.markers.length > 0 &&
          this.centerPoint.lat &&
          this.centerPoint.lng
        ) {
          const oldCentroidLat =
            this.markers.reduce((sum, m) => sum + m.lat, 0) /
            this.markers.length;
          const oldCentroidLng =
            this.markers.reduce((sum, m) => sum + m.lng, 0) /
            this.markers.length;

          const newCentroidLat =
            newMarkers.reduce((sum, m) => sum + m.lat, 0) / newMarkers.length;
          const newCentroidLng =
            newMarkers.reduce((sum, m) => sum + m.lng, 0) / newMarkers.length;

          const deltaLat = newCentroidLat - oldCentroidLat;
          const deltaLng = newCentroidLng - oldCentroidLng;

          this.centerPoint = {
            lat: this.centerPoint.lat + deltaLat,
            lng: this.centerPoint.lng + deltaLng,
          };
          this.$emit("center-changed", this.centerPoint);
          this.reverseGeocode(this.centerPoint);
        }

        this.markers = newMarkers;
        this.$emit("area-changed", this.markers);
      }
    },

    onCenterDrag(e) {
      this.centerPoint = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
    },

    onCenterDragEnd(e) {
      this.centerPoint = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      this.$emit("center-changed", this.centerPoint);
      this.reverseGeocode(this.centerPoint);
    },

    removePoint(index) {
      if (this.readonly || this.activeMode === "move") return;
      this.markers.splice(index, 1);
      this.$emit("area-changed", this.markers);
    },

    undoLastPoint() {
      if (this.markers.length > 0) {
        this.markers.pop();
        this.$emit("area-changed", this.markers);
      }
    },

    clearAllPoints() {
      this.markers = [];
      this.$emit("area-changed", this.markers);
    },

    calculateAndSetCentroid() {
      if (this.markers.length === 0) return;
      let sumLat = 0;
      let sumLng = 0;
      this.markers.forEach((p) => {
        sumLat += p.lat;
        sumLng += p.lng;
      });
      this.centerPoint = {
        lat: sumLat / this.markers.length,
        lng: sumLng / this.markers.length,
      };
      this.$emit("center-changed", this.centerPoint);
    },

    fitBoundsToPolygon() {
      if (!this.$refs.gmap || this.markers.length === 0) return;
      this.$refs.gmap.$mapPromise.then((map) => {
        const bounds = new google.maps.LatLngBounds();
        this.markers.forEach((p) => {
          bounds.extend(new google.maps.LatLng(p.lat, p.lng));
        });
        if (this.centerPoint.lat && this.centerPoint.lng) {
          bounds.extend(
            new google.maps.LatLng(this.centerPoint.lat, this.centerPoint.lng)
          );
        }
        map.fitBounds(bounds);
      });
    },

    reverseGeocode(point) {
      if (
        typeof google === "undefined" ||
        !google.maps ||
        !google.maps.Geocoder
      )
        return;
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: point }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          this.$emit("location-changed", results[0].formatted_address);
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.district-map-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  font-family: inherit;

  .map-controls-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: #ffffff;
    padding: 10px 14px;
    border-radius: 12px;
    border: 1px solid rgba(120, 61, 115, 0.15);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

    .search-box-wrapper {
      flex: 1 1 280px;
      min-width: 250px;

      .search-input-inner {
        position: relative;
        display: flex;
        align-items: center;

        .search-icon {
          position: absolute;
          left: 12px;
          color: #783d73;
          font-size: 14px;
          pointer-events: none;
        }

        .map-search-input {
          width: 100%;
          height: 40px;
          padding-left: 36px;
          padding-right: 14px;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          font-size: 13.5px;
          transition: all 0.2s ease;

          &:focus {
            border-color: #783d73;
            box-shadow: 0 0 0 3px rgba(120, 61, 115, 0.1);
            outline: none;
          }
        }
      }
    }

    .controls-toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;

      .mode-toggle-group {
        display: flex;
        background: #f4f5f8;
        padding: 3px;
        border-radius: 8px;

        .mode-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          font-size: 13px;
          font-weight: 600;
          border: none;
          border-radius: 6px;
          background: transparent;
          color: #666;
          cursor: pointer;
          transition: all 0.2s ease;

          i {
            font-size: 13px;
          }

          &.active {
            background: #783d73;
            color: #ffffff;
            box-shadow: 0 2px 6px rgba(120, 61, 115, 0.25);
          }

          &:hover:not(.active):not(:disabled) {
            color: #783d73;
            background: rgba(120, 61, 115, 0.08);
          }

          &:disabled {
            opacity: 0.45;
            cursor: not-allowed;
          }
        }
      }

      .action-buttons-group {
        display: flex;
        align-items: center;
        gap: 6px;

        .btn-tool {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          padding: 6px 12px;
          height: 36px;
          font-size: 13px;
          font-weight: 600;
          border-radius: 8px;
          border: 1px solid transparent;
          cursor: pointer;
          transition: all 0.2s ease;

          &:disabled {
            opacity: 0.45;
            cursor: not-allowed;
          }

          &.btn-undo {
            background: #fff3e0;
            color: #e65100;
            border-color: #ffe0b2;

            &:hover:not(:disabled) {
              background: #ffe0b2;
            }
          }

          &.btn-clear {
            background: #ffebee;
            color: #c62828;
            border-color: #ffcdd2;

            &:hover:not(:disabled) {
              background: #ffcdd2;
            }
          }

          &.btn-center {
            background: #ede7f6;
            color: #512da8;
            border-color: #d1c4e9;
            padding: 6px 10px;

            &:hover:not(:disabled) {
              background: #d1c4e9;
            }
          }
        }
      }
    }
  }

  .map-status-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 10px;
    background: #fbfbfd;
    border-radius: 8px;
    font-size: 12.5px;

    .points-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      border-radius: 20px;
      font-weight: 600;

      &.badge-valid {
        background: #e8f5e9;
        color: #2e7d32;
        border: 1px solid #c8e6c9;
      }

      &.badge-warning {
        background: #fff8e1;
        color: #f57f17;
        border: 1px solid #ffe082;
      }
    }

    .center-info-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      background: #f5f5f5;
      border-radius: 20px;
      font-size: 12px;
      color: #424242;
    }

    .mode-hint {
      color: #616161;
      display: flex;
      align-items: center;
    }
  }

  .map-container-frame {
    position: relative;
    width: 100%;
    border-radius: 14px;
    overflow: hidden;
    border: 2px solid rgba(120, 61, 115, 0.15);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);

    &.mode-move-active {
      border-color: #2e7d32;
      box-shadow: 0 4px 16px rgba(46, 125, 50, 0.15);
    }

    ::v-deep .vue-map-container {
      width: 100%;
      height: 100%;
    }
  }
}
</style>
